import { MenuSchedule, WeeklySchedule, TimeSlot } from '../types/menuSchedule.types';

export interface MenuStatusResult {
  isOpen: boolean;
  nextOpening: Date | null;
  nextClosing: Date | null;
  currentDaySchedule: TimeSlot[] | null;
}

/**
 * Calculate if a menu is currently open and when it will next open/close
 * based on the schedule and current local time.
 * 
 * @param schedule - The menu schedule configuration
 * @returns MenuStatusResult with current status and next events
 */
export function calculateMenuStatus(schedule: MenuSchedule | null): MenuStatusResult {
  // Default result when no schedule (menu is always open)
  if (!schedule || !schedule.weekly_schedule) {
    return {
      isOpen: true,
      nextOpening: null,
      nextClosing: null,
      currentDaySchedule: null,
    };
  }

  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes(); // Minutes since midnight
  const currentDayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Map JavaScript day (0=Sunday) to our schedule keys
  const dayMap: { [key: number]: keyof WeeklySchedule } = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  // Check for exceptions first
  const todayDateString = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const exception = schedule.exceptions?.find(exc => exc.date === todayDateString);

  let todaySchedule: TimeSlot[] = [];

  if (exception) {
    // Exception found for today
    if (exception.closed) {
      // Closed all day due to exception
      todaySchedule = [];
    } else if (exception.open && exception.close) {
      // Custom hours for today
      todaySchedule = [{ open: exception.open, close: exception.close }];
    }
  } else {
    // Use regular weekly schedule
    const dayKey = dayMap[currentDayOfWeek];
    todaySchedule = schedule.weekly_schedule[dayKey] || [];
  }

  // Check if currently open
  let isOpen = false;
  let nextClosingToday: number | null = null;

  for (const slot of todaySchedule) {
    const [openHour, openMin] = slot.open.split(':').map(Number);
    const [closeHour, closeMin] = slot.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    if (currentTime >= openTime && currentTime < closeTime) {
      isOpen = true;
      nextClosingToday = closeTime;
      break;
    }
  }

  // Calculate next opening
  let nextOpening: Date | null = null;
  let nextClosing: Date | null = null;

  if (isOpen && nextClosingToday !== null) {
    // Menu is open, calculate next closing today
    nextClosing = new Date(now);
    nextClosing.setHours(Math.floor(nextClosingToday / 60), nextClosingToday % 60, 0, 0);
  } else {
    // Menu is closed, find next opening
    // First check if there's a slot later today
    let foundLaterToday = false;
    for (const slot of todaySchedule) {
      const [openHour, openMin] = slot.open.split(':').map(Number);
      const openTime = openHour * 60 + openMin;
      
      if (openTime > currentTime) {
        nextOpening = new Date(now);
        nextOpening.setHours(openHour, openMin, 0, 0);
        foundLaterToday = true;
        break;
      }
    }

    // If no slot later today, search next 7 days
    if (!foundLaterToday) {
      for (let i = 1; i <= 7; i++) {
        const checkDate = new Date(now);
        checkDate.setDate(checkDate.getDate() + i);
        checkDate.setHours(0, 0, 0, 0);
        
        const checkDayOfWeek = checkDate.getDay();
        const checkDateString = checkDate.toISOString().split('T')[0];
        
        // Check for exception on this future date
        const futureException = schedule.exceptions?.find(exc => exc.date === checkDateString);
        let futureSchedule: TimeSlot[] = [];
        
        if (futureException) {
          if (!futureException.closed && futureException.open && futureException.close) {
            futureSchedule = [{ open: futureException.open, close: futureException.close }];
          }
        } else {
          const futureDayKey = dayMap[checkDayOfWeek];
          futureSchedule = schedule.weekly_schedule[futureDayKey] || [];
        }
        
        if (futureSchedule.length > 0) {
          // Found a day with opening hours
          const firstSlot = futureSchedule[0];
          const [openHour, openMin] = firstSlot.open.split(':').map(Number);
          nextOpening = new Date(checkDate);
          nextOpening.setHours(openHour, openMin, 0, 0);
          break;
        }
      }
    }
  }

  return {
    isOpen,
    nextOpening,
    nextClosing,
    currentDaySchedule: todaySchedule.length > 0 ? todaySchedule : null,
  };
}

/**
 * Format a date to a human-readable string in Spanish
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatDateTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  
  return date.toLocaleString('es-CL', options);
}

/**
 * Format a date to show relative time (e.g., "hoy", "mañana", or the date)
 * @param date - The date to format
 * @returns Formatted string
 */
export function formatRelativeDateTime(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const timeStr = date.toLocaleTimeString('es-CL', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  if (targetDate.getTime() === today.getTime()) {
    return `Hoy a las ${timeStr}`;
  } else if (targetDate.getTime() === tomorrow.getTime()) {
    return `Mañana a las ${timeStr}`;
  } else {
    return date.toLocaleString('es-CL', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
}